// src/components/CueBoardCard.tsx (Final & Corrected)
import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { CueNote } from '../state/notes-store';
import { NotesAction } from '../reducers/notesReducer';
import { Lightbulb, Tags, FileText, Eye } from 'lucide-react';
import NoteBody from './NoteBody';
import Hint from './Hint';
import { applySavedOrAutosize, autosizeAndPersist, saveHeight, autosize } from '../utils/heightPersistence';
import { toggleOrWrap, TOKENS, Token } from '../utils/markdownFormatter';
import './components.css';

type EditKey = number | 'new' | null;

interface CueBoardCardProps {
  note: CueNote;
  index: number;
  dispatch: React.Dispatch<NotesAction>;
  activePacketId: string | null;
  showHints: boolean;
  showKeyPhrases: boolean;
  combinedRefreshToken: number;
}

export default function CueBoardCard({ note, index, dispatch, activePacketId, showHints, showKeyPhrases, combinedRefreshToken }: CueBoardCardProps) {
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [editingTriggerKey, setEditingTriggerKey] = useState<EditKey>(null);
  const [draftTrigger, setDraftTrigger] = useState('');

  useEffect(() => {
    if (bodyRef.current) {
      applySavedOrAutosize(activePacketId, note.id, bodyRef.current);
    }
  }, [activePacketId, note.id]);

  const handleApplyToggle = (tok: Token) => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart, selectionEnd } = el;
    const res = toggleOrWrap(note.body_markdown, selectionStart, selectionEnd, tok);
    dispatch({ type: 'UPDATE_BODY', payload: { id: note.id, body: res.next } });

    requestAnimationFrame(() => {
      if(bodyRef.current) {
        bodyRef.current.focus();
        bodyRef.current.setSelectionRange(res.selStart, res.selEnd);
      }
    });
  };

  const startEdit = (key: EditKey, initialValue: string) => {
    setEditingTriggerKey(key);
    setDraftTrigger(initialValue);
  };

  const cancelEdit = () => {
    setEditingTriggerKey(null);
    setDraftTrigger('');
  };

  const commitEdit = () => {
    if (editingTriggerKey === 'new') {
      dispatch({ type: 'ADD_TRIGGER', payload: { id: note.id, trigger: draftTrigger } });
    } else if (typeof editingTriggerKey === 'number') {
      dispatch({ type: 'UPDATE_TRIGGER', payload: { id: note.id, index: editingTriggerKey, trigger: draftTrigger } });
    }
    cancelEdit();
  };

  const transparentBtnStyle: React.CSSProperties = {
    background: "transparent", border: "none", padding: 0, margin: 0,
    boxShadow: "none", cursor: "pointer", font: "inherit", color: "inherit",
    maxWidth: "100%", textAlign: "left", overflowWrap: "anywhere",
  };

  return (
    <div className="card card-accent hoverable vstack-md cueboard-card">
      <div className="muted small mb-1">#{index + 1}</div>

      <div className="vstack-sm">
        <label htmlFor={`title-${note.id}`} className="label flex items-center">
          <Lightbulb size={16} className="mr-2 text-yellow-500" /> Header / Title
        </label>
        {showHints && <Hint id="header" category="tag" compact refreshToken={combinedRefreshToken}><b>Header</b> is the title that shows in Live. <i>Keep it short and clear.</i></Hint>}
        <div className="actions">
          <input id={`title-${note.id}`} type="text" value={note.title}
            onChange={(e) => dispatch({ type: 'UPDATE_TITLE', payload: { id: note.id, title: e.target.value }})}
            placeholder="e.g., Elevator Pitch" className="input trigger-input flex-1" />
          <button className="button button-secondary" onClick={() => dispatch({ type: 'REMOVE_NOTE', payload: { id: note.id } })} title="Remove CueBoard">Remove</button>
        </div>
      </div>

      <div className="vstack-sm">
        <label htmlFor={`body-${note.id}`} className="label flex items-center">
          <FileText size={16} className="mr-2 text-gray-500" /> Notes{" "}
          <span className="muted small" style={{ marginLeft: 6 }}>(start lines with "<code>-</code>" for bullets)</span>
        </label>
        {showHints && <Hint id="notes" category="tag" compact refreshToken={combinedRefreshToken}>Notes render in Live exactly as written. <b><i>Use bullets for clarity.</i></b> You can nest bullets for sub-points <b><i>(order matters for alignment)</i></b>.
</Hint>}
        <div className="muted small" style={{ display: "flex", gap: 6 }}>
          <span className="small" aria-hidden>Format:</span>
          <button type="button" className="button button-ghost" title="Bold (Ctrl/Cmd+B)" onClick={() => handleApplyToggle(TOKENS[0])}><b>B</b></button>
          <button type="button" className="button button-ghost" title="Italic (Ctrl/Cmd+I)" onClick={() => handleApplyToggle(TOKENS[1])}><i>I</i></button>
          <button type="button" className="button button-ghost" title="Highlight (Ctrl/Cmd+H)" onClick={() => handleApplyToggle(TOKENS[2])}><span style={{ background: "rgba(255, 235, 59, .8)", padding: "0 2px", borderRadius: 2 }}>HL</span></button>
        </div>
        <textarea
          id={`body-${note.id}`}
          ref={bodyRef}
          value={note.body_markdown}
          onChange={(e) => {
              dispatch({ type: 'UPDATE_BODY', payload: { id: note.id, body: e.target.value }});
              requestAnimationFrame(() => {
                if (activePacketId) {
                  autosizeAndPersist(activePacketId, note.id, bodyRef.current);
                } else {
                  autosize(bodyRef.current);
                }
              });
          }}
          onKeyDown={(e) => {
              const isMeta = e.metaKey || e.ctrlKey;
              if (!isMeta) return;
              const key = e.key.toLowerCase();
              if (key === "b") { e.preventDefault(); handleApplyToggle(TOKENS[0]); }
              else if (key === "i") { e.preventDefault(); handleApplyToggle(TOKENS[1]); }
              else if (key === "h") { e.preventDefault(); handleApplyToggle(TOKENS[2]); }
          }}
          onBlur={() => saveHeight(activePacketId, note.id, bodyRef.current?.clientHeight ?? 0)}
          placeholder={`- Summarize your years of experience\n- Call out 2–3 key strengths\n- End with why you’re a fit`}
          className="input trigger-input"
          rows={3}
        />
        <div className="vstack-sm" style={{ marginTop: 16 }}>
          <div className="muted small flex items-center">
            <Eye size={16} className="mr-2" /> Live Preview
          </div>
          <div className="note-body">
            <NoteBody text={note.body_markdown} />
          </div>
        </div>
      </div>

      <div className="vstack-sm" style={{ marginTop: 8 }}>
        <label className="label flex items-center">
          <Tags size={16} className="mr-2" /> Trigger Phrases
        </label>
        {showHints && <Hint id="triggers" category="tag" compact refreshToken={combinedRefreshToken}><b>Triggers</b> are phrases CueBoard listens for. <i>The app auto-generates them. Add your own if you'd like!</i></Hint>}
        {showKeyPhrases ? (
          <div className="chip-wrap">
            {(note.triggers ?? []).map((t, idx) => (
              <div key={`${note.id}-chip-${idx}`} className="chip">
                {editingTriggerKey === idx ? (
                  <input autoFocus value={draftTrigger}
                    onChange={(e) => setDraftTrigger(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                      else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                    }}
                    onBlur={commitEdit}
                    className="input chip-input"
                  />
                ) : (
                  <>
                    <button type="button" onClick={() => startEdit(idx, t)} style={transparentBtnStyle} aria-label={`Edit trigger: ${t}`}>
                      {t}
                    </button>
                    <button type="button" onClick={() => dispatch({ type: 'REMOVE_TRIGGER', payload: { id: note.id, index: idx }})} style={{...transparentBtnStyle, marginLeft: '4px', padding: '0 4px' }} aria-label="Remove trigger">
                      ×
                    </button>
                  </>
                )}
              </div>
            ))}
            {editingTriggerKey === 'new' ? (
              <div className="chip">
                <input autoFocus value={draftTrigger}
                  onChange={(e) => setDraftTrigger(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                  }}
                  onBlur={commitEdit}
                  className="input chip-input" placeholder="New trigger…"
                />
              </div>
            ) : (
              <button type="button" className="button button-secondary" onClick={() => startEdit('new', '')}>
                + Add trigger
              </button>
            )}
          </div>
        ) : (
          <div className="small muted" style={{ marginTop: 6 }}>
            <i>Key trigger phrases hidden (toggle above to view)</i>
          </div>
        )}
      </div>
    </div>
  );
}